import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Boxes, Hash, ExternalLink, Award, Copy } from "lucide-react";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";

export default function StudentCertificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/certificates/me");
      setCerts(data);
      setLoading(false);
    })();
  }, []);

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="p-6 md:p-10">
      <BackButton />
      <div className="mt-5 mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          /my credentials
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter">
          Certificates · NFTs
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every certificate is SHA-256 anchored to the simulated Polygon network and publicly verifiable.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-72 animate-pulse border border-border bg-muted" />
          ))}
        </div>
      ) : certs.length === 0 ? (
        <div className="border-2 border-dashed border-foreground/30 p-10 text-center">
          <Award className="mx-auto h-8 w-8 text-muted-foreground" />
          <h3 className="font-display mt-3 text-xl font-extrabold">No certificates yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Complete a course to earn your first blockchain credential.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {certs.map((c) => (
            <CertCard key={c.id} cert={c} onCopy={copy} />
          ))}
        </div>
      )}
    </div>
  );
}

function CertCard({ cert, onCopy }) {
  const verifyUrl = `${window.location.origin}/verify/${cert.cert_hash}`;
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
    verifyUrl
  )}`;
  return (
    <div className="cert-grain relative border-2 border-foreground bg-white p-7 hard-shadow" data-testid={`cert-card-${cert.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            CERTIFICATE OF COMPLETION · {cert.course_category}
          </div>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight tracking-tighter">
            {cert.course_title}
          </h2>
          <div className="mt-3 text-sm">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Awarded to
            </div>
            <div className="font-semibold">{cert.student_name}</div>
          </div>
          <div className="mt-3 mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Issued {new Date(cert.issued_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        </div>
        <div className="flex h-12 w-12 items-center justify-center bg-foreground text-background">
          <Boxes className="h-6 w-6" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Certificate Hash
            </div>
            <div className="mt-1 flex items-center gap-2 break-all border border-foreground/15 bg-muted px-2 py-1.5 font-mono text-[10px]">
              <Hash className="h-3 w-3 shrink-0" />
              <span className="flex-1">{cert.cert_hash}</span>
              <button onClick={() => onCopy(cert.cert_hash)} data-testid={`copy-hash-${cert.id}`}>
                <Copy className="h-3 w-3 hover:text-primary" />
              </button>
            </div>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Transaction
            </div>
            <div className="mt-1 break-all border border-foreground/15 bg-muted px-2 py-1.5 font-mono text-[10px]">
              {cert.txn_hash}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Block
              </div>
              <div className="mt-1 font-mono text-xs">#{cert.block_number.toLocaleString()}</div>
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Network
              </div>
              <div className="mt-1 font-mono text-xs">{cert.network}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end justify-between">
          <img src={qr} alt="verify QR" className="h-24 w-24 border border-foreground/20" />
          <a
            href={verifyUrl}
            target="_blank"
            rel="noreferrer"
            className="mono mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary hover:underline"
            data-testid={`verify-link-${cert.id}`}
          >
            verify <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
